// gridcopy — pure formatters that turn a rectangular grid selection into JSON,
// a Markdown table, or SQL INSERT statements for the clipboard. No DOM imports,
// so it's unit-testable in plain Node; Grid.jsx extracts the {colNames, matrix}
// and calls these.
//
// matrix is an array of rows; each row is an array of cell values aligned to
// colNames (a value is null for SQL NULL, otherwise any scalar). numericByCol is
// an optional boolean[] aligned to colNames — true => emit unquoted in INSERTs.

// ── JSON ────────────────────────────────────────────────────────────────────
// Array of objects keyed by column name. Duplicate column names (a join can
// repeat a name) get a "#n" suffix so no key is silently dropped.
export function buildJson(colNames, matrix) {
  const keys = dedupeKeys(colNames);
  const out = matrix.map((row) => {
    const o = {};
    keys.forEach((k, i) => { o[k] = row[i] === undefined ? null : row[i]; });
    return o;
  });
  return JSON.stringify(out, null, 2);
}

function dedupeKeys(names) {
  const seen = {};
  return names.map((n) => {
    const key = n == null ? '' : String(n);
    if (seen[key] === undefined) { seen[key] = 0; return key; }
    seen[key] += 1;
    return `${key}#${seen[key]}`;
  });
}

// ── Markdown table ──────────────────────────────────────────────────────────
export function buildMarkdown(colNames, matrix) {
  const esc = (v) => (v == null ? '' : String(v)).replace(/\\/g, '\\\\').replace(/\|/g, '\\|').replace(/\r?\n/g, '<br>');
  const header = `| ${colNames.map(esc).join(' | ')} |`;
  const sep = `| ${colNames.map(() => '---').join(' | ')} |`;
  const body = matrix.map((row) => `| ${colNames.map((_, i) => esc(row[i])).join(' | ')} |`);
  return [header, sep, ...body].join('\n');
}

// ── SQL INSERT ──────────────────────────────────────────────────────────────
// sqlEscaper MUST match internal/export's sqlStrEscaper so a copied INSERT and
// a server-side dump/import escape identically.
const SQL_REPLACEMENTS = [
  ['\\', '\\\\'], ["'", "\\'"], ['\0', '\\0'], ['\n', '\\n'], ['\r', '\\r'], ['\x1a', '\\Z'],
];
function sqlEscape(s) {
  let r = String(s);
  for (const [from, to] of SQL_REPLACEMENTS) r = r.split(from).join(to);
  return r;
}

const NUMERIC_RE = /^-?(?:\d+|\d*\.\d+)$/;

export function sqlLiteral(v, numeric) {
  if (v == null) return 'NULL';
  const s = String(v);
  if (numeric && NUMERIC_RE.test(s)) return s;
  return `'${sqlEscape(s)}'`;
}

export function backtick(s) {
  return '`' + String(s == null ? '' : s).replace(/`/g, '``') + '`';
}

// Extended multi-row INSERT (one statement). table is a bare table name.
export function buildInserts(table, colNames, matrix, numericByCol) {
  if (!matrix.length) return '';
  const cols = colNames.map(backtick).join(', ');
  // No space between tuple values (matches internal/export's extended INSERT).
  const tuples = matrix.map((row) =>
    '(' + colNames.map((_, i) => sqlLiteral(row[i], !!(numericByCol && numericByCol[i]))).join(',') + ')');
  return `INSERT INTO ${backtick(table)} (${cols}) VALUES\n${tuples.join(',\n')};`;
}

// numericType reports whether a MySQL column type string is numeric (→ unquoted
// literals). Bit/year included; decimal/float kept exact as text, still unquoted.
export function numericType(type) {
  return /^\s*(tinyint|smallint|mediumint|int|integer|bigint|decimal|dec|numeric|float|double|real|bit|year)\b/i.test(type || '');
}
